import React, { useEffect, useImperativeHandle, forwardRef, useRef, RefForwardingComponent, RefObject } from 'react';

// styles
import { Fieldset } from './styles/form';

// context
import { useRequestParams } from 'js/common/context/RequestParamsContext';

declare global {
  interface Window {
    grecaptcha?: ReCaptchaInstance;
    Recaptcha?: ReCaptchaInstance;
    reCaptchaOnload: () => void;
  }
}

interface ReCaptchaInstance {
  ready: (cb: () => any) => void;
  execute: () => Promise<string>;
  render: (id: string, options: ReCaptchaRenderOptions) => any;
  getResponse: () => string;
  reset: () => void;
  reload: () => void;
}

type Callback = (response: any) => void;

interface ReCaptchaRenderOptions {
  siteKey: string;
  badge: string;
  callback: Callback;
}

interface ReCaptchaProps {
  callback?: Callback;
  invisible?: boolean;
}

export interface ReCaptchaResponse {
  errorCode: string;
  response?: string;
}

export interface ReCaptchaHandles {
  execute: () => void;
  reset: () => void;
  verify: () => ReCaptchaResponse;
}

const ReCaptcha: RefForwardingComponent<ReCaptchaHandles, ReCaptchaProps> = (
  { callback = () => {}, invisible = false },
  ref: RefObject<ReCaptchaHandles>
) => {
  const { reCaptchaPublic, reCaptchaEnabledRegistration, reCaptchaEnabledLogin } = useRequestParams();

  useEffect(() => {
    // There is a race condition between the captcha js loading and the react component rendering.
    // If the js loads first, then the react component goes ahead and renders the captcha. Otherwise,
    // the componentDidMount() function populates the reCaptchaOnload function in registration/index.jsp
    // so that the captcha is rendered after the script is loaded.

    const renderRecaptcha = () => {
      window.grecaptcha &&
        window.grecaptcha.render('g-recaptcha', {
          siteKey: reCaptchaPublic,
          // Position bottomleft so we don't overlap Intercom widget
          badge: 'bottomleft',
          callback,
        });
    };

    if (reCaptchaEnabledRegistration || reCaptchaEnabledLogin) {
      if (typeof window.grecaptcha !== 'undefined' && typeof window.grecaptcha.render === 'function') {
        renderRecaptcha();
      } else {
        window.reCaptchaOnload = renderRecaptcha;
      }
    }
  }, [reCaptchaEnabledRegistration, reCaptchaPublic, reCaptchaEnabledLogin]);

  const reCaptchaRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => ({
    execute: () => {
      if (typeof window.grecaptcha !== 'undefined' && typeof window.grecaptcha.execute === 'function') {
        window.grecaptcha.execute();
      }
    },
    reset: () => {
      // Support both version 1 and 2.
      if (window.grecaptcha) {
        window.grecaptcha.reset();
      }
      if (window.Recaptcha) {
        window.Recaptcha && window.Recaptcha.reload();
      }
    },
    verify: () => {
      const reCaptchaResponse = window.grecaptcha && window.grecaptcha.getResponse();

      const result = {
        errorCode: '',
        response: reCaptchaResponse,
      };

      if (reCaptchaResponse === '') {
        result.errorCode = 'MISSING_CAPTCHA_RESPONSE';
      }

      return result;
    },
  }));

  return (
    <Fieldset>
      {invisible ? (
        // Login uses invisible captcha which is loaded with `data-size="invisible"`
        <div id="g-recaptcha" data-sitekey={reCaptchaPublic} data-size="invisible" ref={reCaptchaRef} />
      ) : (
        <div id="g-recaptcha" data-sitekey={reCaptchaPublic} data-badge="bottomleft" ref={reCaptchaRef} />
      )}
    </Fieldset>
  );
};

export default forwardRef(ReCaptcha);
